import React, { useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { filterCompetitionsByName, aggregateCompetitionEntries } from '../utils/competitionsDrilldown';

const C = ['#0B3D91', '#E31837', '#2196F3', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899', '#4FC3F7'];

const PERSON_FIELDS = [
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['role', 'Role'],
  ['gender', 'Gender'],
  ['region', 'Region'],
  ['qualification_level', 'Qualification'],
  ['qualification_date', 'Qualification date'],
  ['currently_active', 'Currently active'],
  ['nationality', 'Nationality'],
  ['club_association', 'Club / association'],
  ['email', 'Email'],
  ['mobile', 'Mobile'],
];

/**
 * Drill-down for Competition Analytics (top competition) and People register row.
 */
export default function CompetitionsTabDrillDownModal({ open, onClose, variant, competitionName, person, competitions }) {
  const compRows = useMemo(() => {
    if (!open || variant !== 'competition' || !competitionName) return [];
    return filterCompetitionsByName(competitions, competitionName);
  }, [open, variant, competitionName, competitions]);

  const agg = useMemo(() => aggregateCompetitionEntries(compRows), [compRows]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !variant) return null;

  return (
    <div
      className="drill-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="drill-modal-panel glass-card drill-modal-panel--wide" role="dialog" aria-modal="true">
        <div className="drill-modal-header">
          <div>
            <h2 id="comp-drill-title" className="drill-modal-title">
              {variant === 'competition' ? competitionName : `${person?.first_name || ''} ${person?.last_name || ''}`.trim()}
            </h2>
            <p className="drill-modal-sub">
              {variant === 'competition'
                ? 'Competition template entries (team rows) for this competition name'
                : 'People register (Umpires & Coaches template)'}
            </p>
          </div>
          <button type="button" className="drill-modal-close" onClick={onClose} aria-label="Close">
            <X size={22} />
          </button>
        </div>

        {variant === 'competition' && (
          <>
            <div className="drill-modal-stats">
              <div className="drill-stat">
                <span className="drill-stat-val">{agg.rowCount}</span>
                <span className="drill-stat-lbl">Team entries</span>
              </div>
              <div className="drill-stat">
                <span className="drill-stat-val">{agg.teams.length}</span>
                <span className="drill-stat-lbl">Unique teams</span>
              </div>
            </div>

            <div className="drill-modal-charts">
              <div className="drill-chart-block">
                <h3>Entries by region</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={agg.byRegion} margin={{ bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={55} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                    <Bar dataKey="value" fill="#0B3D91" name="Entries" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="drill-pies">
                <div className="drill-chart-block drill-pie">
                  <h3>Age group</h3>
                  {agg.byAge.length === 0 ? (
                    <p className="drill-empty">No age data</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={agg.byAge}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={75}
                          paddingAngle={2}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {agg.byAge.map((_, i) => (
                            <Cell key={i} fill={C[i % C.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => v.toLocaleString()} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div className="drill-chart-block drill-pie">
                  <h3>Ball type</h3>
                  {agg.byBall.length === 0 ? (
                    <p className="drill-empty">No ball type</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={agg.byBall}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={75}
                          paddingAngle={2}
                          label={({ name, percent }) =>
                            `${String(name).slice(0, 16)} ${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {agg.byBall.map((_, i) => (
                            <Cell key={i} fill={C[i % C.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => v.toLocaleString()} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="drill-chart-block">
                <h3>Category (cricket type · gender)</h3>
                <ResponsiveContainer width="100%" height={Math.max(200, agg.byCategoryArr.length * 28)}>
                  <BarChart data={agg.byCategoryArr} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={200} />
                    <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                    <Bar dataKey="value" fill="#2196F3" radius={[0, 4, 4, 0]} name="Entries" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="glass-card" style={{ padding: 0, overflow: 'auto', marginTop: '0.5rem' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Team</th>
                      <th>Region</th>
                      <th>Type</th>
                      <th>Gender</th>
                      <th>Ball</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compRows.slice(0, 100).map((r, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 500 }}>{r.team_name || '—'}</td>
                        <td>{r.region || '—'}</td>
                        <td>{r.cricket_type || '—'}</td>
                        <td>{r.gender || '—'}</td>
                        <td style={{ fontSize: '0.78rem' }}>{r.ball_type || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {compRows.length > 100 && (
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', padding: '0.5rem 1rem' }}>
                    Showing first 100 of {compRows.length} entries.
                  </p>
                )}
              </div>
            </div>
          </>
        )}

        {variant === 'person' && person && (
          <div className="drill-person-grid">
            {PERSON_FIELDS.map(([key, label]) => {
              const val = person[key];
              const display = val === null || val === undefined || val === '' ? '—' : String(val);
              const sensitive = key === 'email' || key === 'mobile';
              return (
                <div key={key} className="drill-person-field">
                  <span className="drill-person-label">{label}</span>
                  <span className={`drill-person-value ${sensitive ? 'drill-sensitive' : ''}`}>{display}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
